import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, User, Eye, EyeOff, Loader, AlertCircle, Sparkles, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ThemePickerStrip from './theme/ThemePickerStrip';
import api from '../services/api';

type AuthMode = 'login' | 'register' | 'forgot' | 'reset';

const AuthModal: React.FC = () => {
  const { showAuthModal, authModalTab, authModalMessage, closeAuthModal, login, register } = useAuth();
  const panelRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<AuthMode>(authModalTab);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [showConfirmField, setShowConfirmField] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  React.useEffect(() => {
    setMode(authModalTab);
    setError('');
    setInfo(authModalMessage || '');
    setName('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setResetCode('');
    setShowConfirmField(false);
  }, [authModalTab, showAuthModal, authModalMessage]);

  const resetConfirmState = () => {
    setConfirmPassword('');
    setShowConfirmField(false);
  };

  const passwordsMatch =
    (mode === 'register' || mode === 'reset') &&
    confirmPassword.length > 0 &&
    password === confirmPassword;

  const passwordsMismatch =
    (mode === 'register' || mode === 'reset') &&
    showConfirmField &&
    confirmPassword.length > 0 &&
    password !== confirmPassword;

  const handlePasswordBlur = () => {
    if ((mode === 'register' || mode === 'reset') && password.length >= 1) {
      setShowConfirmField(true);
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (value.length === 0) {
      resetConfirmState();
    }
  };

  useEffect(() => {
    if (!showAuthModal) return;
    const t = window.setTimeout(() => {
      const el = panelRef.current?.querySelector<HTMLElement>('form input');
      el?.focus();
    }, 80);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAuthModal();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [showAuthModal, closeAuthModal]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const result = await login(email, password);
        if (!result.success) setError(result.error || 'Bir hata oluştu');
        return;
      }

      if (mode === 'forgot') {
        await api.forgotPassword(email);
        setInfo('Sıfırlama kodu e-posta adresinize gönderildi (kayıtlıysa).');
        setMode('reset');
        return;
      }

      if (mode === 'reset') {
        if (resetCode.length !== 6) {
          setError('6 haneli kodu girin');
          return;
        }
        if (password.length < 6) {
          setError('Şifre en az 6 karakter olmalıdır');
          return;
        }
        if (!showConfirmField) {
          setShowConfirmField(true);
          setError('Lütfen şifrenizi tekrar girin');
          return;
        }
        if (password !== confirmPassword) {
          setError('Şifreler eşleşmiyor');
          return;
        }
        await api.resetPassword(email, resetCode, password);
        setInfo('Şifreniz güncellendi. Giriş yapabilirsiniz.');
        setMode('login');
        setPassword('');
        setConfirmPassword('');
        setResetCode('');
        resetConfirmState();
        return;
      }

      // register
      if (name.trim().length < 2) {
        setError('İsim en az 2 karakter olmalıdır');
        return;
      }
      if (!showConfirmField && password.length >= 1) {
        setShowConfirmField(true);
        setError('Lütfen şifrenizi tekrar girin');
        return;
      }
      if (password !== confirmPassword) {
        setShowConfirmField(true);
        setError('Şifreler eşleşmiyor');
        return;
      }
      const result = await register(name, email, password);
      if (!result.success) setError(result.error || 'Bir hata oluştu');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px 14px 44px',
    borderRadius: '12px',
    border: '1px solid rgba(51, 65, 85, 0.3)',
    background: 'rgba(15, 23, 42, 0.6)',
    color: 'white',
    fontSize: '15px',
    outline: 'none',
    transition: 'border-color 0.3s ease',
    boxSizing: 'border-box' as const,
  };

  return (
    <AnimatePresence>
      {showAuthModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeAuthModal}
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            padding: '16px',
          }}
        >
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="auth-modal-title"
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 30 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '440px',
              background: 'rgba(15, 23, 42, 0.95)',
              backdropFilter: 'blur(40px)',
              borderRadius: '24px',
              border: '1px solid rgba(51, 65, 85, 0.2)',
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.5), 0 0 40px var(--accent-primary-soft)',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {/* Top glow */}
            <div style={{
              position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)',
              width: '200px', height: '120px',
              background: 'radial-gradient(circle, var(--accent-primary-soft) 0%, transparent 70%)',
              pointerEvents: 'none',
            }} />

            {/* Close button */}
            <motion.button
              type="button"
              onClick={closeAuthModal}
              aria-label="Kapat"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              style={{
                position: 'absolute', top: '16px', right: '16px', zIndex: 10,
                background: 'rgba(51, 65, 85, 0.3)', border: 'none', borderRadius: '10px',
                padding: '8px', cursor: 'pointer', color: '#94a3b8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <X size={18} />
            </motion.button>

            <div style={{ padding: '40px 32px 32px' }}>
              {/* Header */}
              <div style={{ textAlign: 'center' as const, marginBottom: '28px' }}>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', delay: 0.1 }}
                  style={{
                    width: '52px', height: '52px', borderRadius: '16px',
                    background: 'var(--gradient-logo)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 16px auto',
                    boxShadow: '0 8px 25px var(--accent-primary-border)',
                  }}
                >
                  <Sparkles size={24} color="white" />
                </motion.div>
                <h2 id="auth-modal-title" style={{
                  fontSize: '1.5rem', fontWeight: '800', color: 'white', margin: '0 0 6px 0',
                  fontFamily: "'Space Grotesk', sans-serif",
                }}>
                  {mode === 'login' && 'Hoş Geldin!'}
                  {mode === 'register' && 'Aramıza Katıl'}
                  {mode === 'forgot' && 'Şifremi Unuttum'}
                  {mode === 'reset' && 'Yeni Şifre Belirle'}
                </h2>
                <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
                  {mode === 'login' && 'Notlarına erişmek için giriş yap'}
                  {mode === 'register' && 'Ücretsiz hesap oluştur ve notlara eriş'}
                  {mode === 'forgot' && 'E-posta adresinize sıfırlama kodu göndereceğiz'}
                  {mode === 'reset' && 'E-postanıza gelen kodu ve yeni şifrenizi girin'}
                </p>
              </div>

              {(mode === 'login' || mode === 'register') && (
              <div style={{
                display: 'flex', borderRadius: '12px', padding: '4px',
                background: 'rgba(30, 41, 59, 0.5)', marginBottom: '24px',
                border: '1px solid rgba(51, 65, 85, 0.15)',
              }}>
                {(['login', 'register'] as const).map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() => { setMode(t); setError(''); setInfo(''); resetConfirmState(); }}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '10px', border: 'none',
                      background: mode === t ? 'var(--accent-primary-soft)' : 'transparent',
                      color: mode === t ? 'var(--accent-primary)' : '#64748b',
                      fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {t === 'login' ? 'Giriş Yap' : 'Kayıt Ol'}
                  </button>
                ))}
              </div>
              )}

              {info && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '12px 14px', borderRadius: '10px', marginBottom: '16px',
                  background: 'var(--accent-primary-soft)', border: '1px solid var(--accent-primary-border)',
                  color: 'var(--accent-primary)', fontSize: '13px', fontWeight: '500',
                }}>
                  <Check size={16} />
                  <span>{info}</span>
                </div>
              )}

              {/* Error */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -10, height: 0 }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '12px 14px', borderRadius: '10px', marginBottom: '16px',
                      background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)',
                      color: '#ef4444', fontSize: '13px', fontWeight: '500',
                    }}
                  >
                    <AlertCircle size={16} />
                    <span>{error}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Form */}
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* Name field (register only) */}
                <AnimatePresence>
                  {mode === 'register' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      style={{ position: 'relative', overflow: 'hidden' }}
                    >
                      <User size={18} color="#64748b" style={{ position: 'absolute', left: '14px', top: '14px', zIndex: 1 }} />
                      <input
                        type="text"
                        placeholder="İsim Soyisim"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        style={inputStyle}
                        required
                        minLength={2}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Email */}
                <div style={{ position: 'relative' }}>
                  <Mail size={18} color="#64748b" style={{ position: 'absolute', left: '14px', top: '14px', zIndex: 1 }} />
                  <input
                    type="email"
                    placeholder="E-posta adresi"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={inputStyle}
                    required
                  />
                </div>

                {mode === 'reset' && (
                  <div style={{ position: 'relative' }}>
                    <Lock size={18} color="#64748b" style={{ position: 'absolute', left: '14px', top: '14px', zIndex: 1 }} />
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="6 haneli kod"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      style={inputStyle}
                      required={mode === 'reset'}
                      maxLength={6}
                    />
                  </div>
                )}

                {(mode === 'login' || mode === 'register' || mode === 'reset') && (
                <div style={{ position: 'relative' }}>
                  <Lock size={18} color="#64748b" style={{ position: 'absolute', left: '14px', top: '14px', zIndex: 1 }} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Şifre"
                    value={password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    onBlur={handlePasswordBlur}
                    style={{ ...inputStyle, paddingRight: '44px' }}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute', right: '12px', top: '12px',
                      background: 'none', border: 'none', cursor: 'pointer', color: '#64748b',
                      padding: '2px', display: 'flex',
                    }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                )}

                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => { setMode('forgot'); setError(''); setInfo(''); }}
                    style={{
                      alignSelf: 'flex-end',
                      background: 'none',
                      border: 'none',
                      color: '#94a3b8',
                      fontSize: '13px',
                      cursor: 'pointer',
                      padding: 0,
                      marginTop: '-6px',
                    }}
                  >
                    Şifremi unuttum
                  </button>
                )}

                {/* Confirm password (register / reset) */}
                <AnimatePresence>
                  {(mode === 'register' || mode === 'reset') && showConfirmField && (
                    <motion.div
                      initial={{ opacity: 0, height: 0, y: -8 }}
                      animate={{ opacity: 1, height: 'auto', y: 0 }}
                      exit={{ opacity: 0, height: 0, y: -8 }}
                      transition={{ type: 'spring', damping: 22, stiffness: 280 }}
                      style={{ position: 'relative', overflow: 'hidden' }}
                    >
                      <Lock
                        size={18}
                        color={passwordsMatch ? 'var(--accent-primary)' : passwordsMismatch ? '#ef4444' : '#64748b'}
                        style={{ position: 'absolute', left: '14px', top: '14px', zIndex: 1 }}
                      />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Şifre tekrar"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        aria-invalid={passwordsMismatch}
                        aria-describedby={passwordsMismatch ? 'confirm-password-error' : undefined}
                        style={{
                          ...inputStyle,
                          paddingRight: '72px',
                          borderColor: passwordsMatch
                            ? 'var(--accent-primary-glow)'
                            : passwordsMismatch
                              ? 'rgba(239, 68, 68, 0.6)'
                              : 'rgba(51, 65, 85, 0.3)',
                          boxShadow: passwordsMatch
                            ? '0 0 0 1px var(--shadow-glow)'
                            : passwordsMismatch
                              ? '0 0 0 1px rgba(239, 68, 68, 0.25)'
                              : undefined,
                        }}
                        required
                        minLength={6}
                      />
                      {passwordsMatch && (
                        <Check
                          size={18}
                          color="var(--accent-primary)"
                          style={{ position: 'absolute', right: '44px', top: '14px', zIndex: 1 }}
                          aria-hidden
                        />
                      )}
                      <button
                        type="button"
                        aria-label={showConfirmPassword ? 'Şifre tekrarını gizle' : 'Şifre tekrarını göster'}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          position: 'absolute', right: '12px', top: '12px',
                          background: 'none', border: 'none', cursor: 'pointer', color: '#64748b',
                          padding: '2px', display: 'flex',
                        }}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                      {passwordsMismatch && (
                        <p
                          id="confirm-password-error"
                          style={{
                            margin: '6px 0 0 4px',
                            fontSize: '12px',
                            color: '#ef4444',
                            fontWeight: 500,
                          }}
                        >
                          Şifreler eşleşmiyor
                        </p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Submit */}
                <motion.button
                  type="submit"
                  disabled={loading}
                  className="btn-shimmer"
                  whileHover={loading ? {} : { scale: 1.02 }}
                  whileTap={loading ? {} : { scale: 0.98 }}
                  style={{
                    width: '100%',
                    padding: '15px',
                    borderRadius: '12px',
                    border: 'none',
                    background: loading
                      ? 'var(--accent-primary-glow)'
                      : 'var(--gradient-primary)',
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: loading ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginTop: '4px',
                    boxShadow: '0 4px 20px var(--accent-primary-border)',
                  }}
                >
                  {loading ? (
                    <>
                      <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                        <Loader size={18} />
                      </motion.div>
                      <span>
                        {mode === 'login' && (loading ? 'Giriş yapılıyor...' : 'Giriş Yap')}
                        {mode === 'register' && (loading ? 'Kaydediliyor...' : 'Hesap Oluştur')}
                        {mode === 'forgot' && (loading ? 'Gönderiliyor...' : 'Kod Gönder')}
                        {mode === 'reset' && (loading ? 'Kaydediliyor...' : 'Şifreyi Güncelle')}
                      </span>
                    </>
                  ) : (
                    <span>
                      {mode === 'login' && 'Giriş Yap'}
                      {mode === 'register' && 'Hesap Oluştur'}
                      {mode === 'forgot' && 'Kod Gönder'}
                      {mode === 'reset' && 'Şifreyi Güncelle'}
                    </span>
                  )}
                </motion.button>
              </form>

              <ThemePickerStrip />

              {mode === 'register' && (
                <p
                  style={{
                    textAlign: 'center',
                    marginTop: '16px',
                    color: 'var(--text-dim, #64748b)',
                    fontSize: '12px',
                    lineHeight: 1.55,
                  }}
                >
                  Kayıt olarak{' '}
                  <Link to="/terms" className="accent-link" onClick={closeAuthModal}>
                    Kullanım Şartları
                  </Link>{' '}
                  ve{' '}
                  <Link to="/privacy" className="accent-link" onClick={closeAuthModal}>
                    Gizlilik Politikası
                  </Link>{' '}
                  metinlerini kabul etmiş olursunuz.
                </p>
              )}

              {/* Footer text */}
              <p style={{
                textAlign: 'center' as const, marginTop: '20px',
                color: '#475569', fontSize: '13px',
              }}>
                {mode === 'login' ? (
                  <>Hesabın yok mu?{' '}
                    <button
                      type="button"
                      onClick={() => { setMode('register'); setError(''); setInfo(''); resetConfirmState(); }}
                      style={{ background: 'none', border: 'none', color: 'var(--accent-primary)', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}
                    >
                      Kayıt Ol
                    </button>
                  </>
                ) : mode === 'register' ? (
                  <>Zaten hesabın var mı?{' '}
                    <button
                      type="button"
                      onClick={() => { setMode('login'); setError(''); setInfo(''); resetConfirmState(); }}
                      style={{ background: 'none', border: 'none', color: 'var(--accent-primary)', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}
                    >
                      Giriş Yap
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => { setMode('login'); setError(''); setInfo(''); resetConfirmState(); }}
                      style={{ background: 'none', border: 'none', color: 'var(--accent-primary)', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}
                    >
                      Giriş ekranına dön
                    </button>
                  </>
                )}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;
